import React, { useState, useEffect, useRef } from 'react';
// import { useNavigate } from 'react-router-dom';
import {
  FiChevronUp,
  FiChevronDown,
  FiRefreshCw,
} from 'react-icons/fi';
import { getAuthContext } from '../../utils/jwt';
import { toast } from 'react-toastify';
import { BASE_URL } from '../../utils/constants';

// Typecast icon components for TypeScript compatibility
const FiChevronDownComponent = FiChevronDown as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiChevronUpComponent = FiChevronUp as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiRefreshCwComponent = FiRefreshCw as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

// Grain type for campaigns - Full range
type CampaignGrainType = 'PT1H' | 'PT24H' | 'PT168H' | 'PT720H' | 'PT8760H';

// Map grain types to API range values
const getRangeFromGrain = (grain: CampaignGrainType): string => {
  switch (grain) {
    case 'PT1H': return 'LAST1H';
    case 'PT24H': return 'LAST24H';
    case 'PT168H': return 'LAST7D';
    case 'PT720H': return 'LAST30D';
    case 'PT8760H': return 'LAST1Y';
    default: return 'LAST24H';
  }
};

// Generate a unique Idempotency-Key
const generateIdempotencyKey = (): string => {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 10);
  return `${timestamp}-${random}`;
};

interface Campaign {
  id: string;
  name: string;
  type: string;
  status: string;
  recipients: string;
  openRate: string;
  clickRate: string;
  sentAt: string;
  triggerType: string;
  action: string;
  rawData?: any;
}

interface CampaignsTableProps {
  campaigns: Campaign[];
  totalCount?: number;
  isLoading?: boolean;
  onRefresh?: () => void;
  selectedGrain?: CampaignGrainType;
  onGrainChange?: (grain: CampaignGrainType) => void;
}

// Returns CSS classes for status badges based on campaign status
const getStatusColor = (status: string) => {
  switch (status) {
    case 'Active':
      return 'bg-green-100 text-green-600';
    case 'Draft':
      return 'bg-red-100 text-red-600';
    case 'Schedule':
      return 'bg-yellow-100 text-yellow-600';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

const CampaignsTable: React.FC<CampaignsTableProps> = ({
  campaigns,
  totalCount = 0,
  isLoading = false,
  onRefresh,
  selectedGrain = 'PT24H',
  onGrainChange
}) => {
  // const navigate = useNavigate();

  // State for managing the active filter tab (All, Active, Draft, Schedule)
  const [activeTab, setActiveTab] = useState<
    'All' | 'Active' | 'Draft' | 'Schedule'
  >('All');
  // State for sorting configuration (key and direction)
  const [sortConfig, setSortConfig] = useState<{
    key: keyof Campaign;
    direction: 'asc' | 'desc';
  } | null>(null);
  // State for current page in pagination - 0-indexed
  const [currentPage, setCurrentPage] = useState(0);
  // State for export loading
  const [isExporting, setIsExporting] = useState(false);

  // Ref to track if component is mounted
  const isMounted = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      // Dismiss all toasts when component unmounts
      toast.dismiss();
    };
  }, []);

  const itemsPerPage = 6;

  // Filters campaigns by status
  const filteredCampaigns = campaigns.filter((c) => {
    if (activeTab !== 'All' && c.status !== activeTab) return false;
    return true;
  });

  // Sorts campaigns based on sortConfig (key and direction)
  const sortedCampaigns = React.useMemo(() => {
    if (!sortConfig) return filteredCampaigns;
    const sorted = [...filteredCampaigns].sort((a, b) => {
      const valA = a[sortConfig.key]?.toString().toLowerCase() ?? '';
      const valB = b[sortConfig.key]?.toString().toLowerCase() ?? '';
      if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredCampaigns, sortConfig]);

  const totalCountFiltered = filteredCampaigns.length;
  const totalPages = Math.ceil(totalCountFiltered / itemsPerPage);

  // Paginates the sorted campaigns - 0-indexed
  const paginatedCampaigns = sortedCampaigns.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  // Calculate which items to show
  const startItem = currentPage * itemsPerPage + 1;
  const endItem = Math.min((currentPage + 1) * itemsPerPage, totalCountFiltered);

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Initiates sorting by a specified column key, toggling between ascending and descending
  const requestSort = (key: keyof Campaign) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === 'asc'
    ) {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Returns the appropriate sort icon (up/down arrow) based on sort configuration
  const getSortIcon = (key: keyof Campaign) => {
    if (sortConfig?.key === key) {
      return sortConfig.direction === 'asc' ? (
        <FiChevronUpComponent size={14} />
      ) : (
        <FiChevronDownComponent size={14} />
      );
    }
    return <FiChevronDownComponent size={14} />;
  };

  // Reset to first page when campaigns change (like filter change)
  useEffect(() => {
    setCurrentPage(0);
  }, [campaigns]);

  // Function to download the exported file
  const downloadExportedFile = async (jobId: string, token: string, tenantId: string, userId: string) => {
    try {
      const downloadUrl = `${BASE_URL}/v1/reporting/exports/${jobId}/download`;


      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to download file: ${response.status}`);
      }

      const blob = await response.blob();
      const url_ = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url_;

      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `campaigns_export_${new Date().toISOString().slice(0, 10)}.csv`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url_);

      // Only show toast if component is still mounted
      if (isMounted.current) {
        toast.success('File downloaded successfully!');
      }

    } catch (error) {
      // console.error('❌ Error downloading file:', error);
      if (isMounted.current) {
        toast.error('Failed to download the exported file');
      }
    }
  };

  // Function to check export status with polling
  const checkExportStatus = async (
    jobId: string,
    token: string,
    tenantId: string,
    userId: string,
    attempt: number = 1
  ) => {
    const maxAttempts = 5;

    try {
      const statusUrl = `${BASE_URL}/v1/reporting/exports/${jobId}`;


      const response = await fetch(statusUrl, {
        method: 'GET',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to get export status: ${response.status}`);
      }

      const statusData = await response.json();

      const currentStatus = statusData.status;

      // If status is not SUCCEEDED, keep polling
      if (currentStatus !== 'SUCCEEDED') {
        if (attempt < maxAttempts) {
          setTimeout(() => {
            // Check again before polling
            if (isMounted.current) {
              checkExportStatus(jobId, token, tenantId, userId, attempt + 1);
            }
          }, 2000);
        } else {
          if (isMounted.current) {
            toast.warning('Export is taking longer than expected. Please check back later.');
            setIsExporting(false);
          }
        }
      } else {
        // Status changed - call download API
        if (isMounted.current) {
          toast.info(`Export ${currentStatus.toLowerCase()}, downloading file...`);

          // Call the download API
          await downloadExportedFile(jobId, token, tenantId, userId);
          setIsExporting(false);
        }
      }
    } catch (error) {
      // console.error('❌ Error checking export status:', error);
      if (isMounted.current) {
        toast.error('Failed to check export status');
        setIsExporting(false);
      }
    }
  };

  // Main export function that calls the API using auth context
  const handleExport = async () => {
    try {
      setIsExporting(true);

      // Get auth context from the JWT utility
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        if (isMounted.current) {
          toast.error('Authentication required. Please log in again.');
        }
        setIsExporting(false);
        return;
      }

      // Get the range based on selected grain
      const range = getRangeFromGrain(selectedGrain);

      // Generate Idempotency-Key
      const idempotencyKey = generateIdempotencyKey();

      // Prepare the request
      const requestBody = {
        type: "campaigns",
        range: range,
        format: "csv",
        limit: 100
      };

      const url = `${BASE_URL}/v1/reporting/exports`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
          'Idempotency-Key': idempotencyKey,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        let errorMessage = `Export failed (${response.status})`;
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      // Parse the response - expecting { jobId: "xxx", status: "QUEUED", downloadUrl: null, error: null }
      const data = await response.json();

      // Get the job ID from the response
      const jobId = data.jobId;

      if (jobId) {
        // Only show toast if component is still mounted
        if (isMounted.current) {
          toast.success('Export started! Checking status...');
          // Start polling
          if (userId && tenantId && token) {
            await checkExportStatus(jobId, token, tenantId, userId);
          } else {
            toast.error('Missing authentication details. Please try again.');
            setIsExporting(false);
          }
        }
      } else {
        if (isMounted.current) {
          toast.info('Export completed!');
          setIsExporting(false);
        }
      }

    } catch (error: any) {
      // console.error('❌ Export error:', error);
      if (isMounted.current) {
        toast.error(error.message || 'Failed to export campaigns. Please try again.');
        setIsExporting(false);
      }
    }
  };

  // Handle grain change
  const handleGrainChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (onGrainChange) {
      onGrainChange(e.target.value as CampaignGrainType);
    }
  };

  // If no data and not loading, show empty state
  if (!isLoading && campaigns.length === 0) {
    return (
      <div className='bg-white rounded-md mt-4 border border-gray-200 shadow-sm'>
        <div className='flex justify-between items-center px-6 pt-4'>
          <div className='flex space-x-6 text-[20px] font-semibold'>
            Campaigns ({totalCount})
          </div>
          <div className='flex items-center space-x-4'>
            <select
              className='border rounded p-2 text-[14px] border-[#E1EAF0] bg-[#F5F8FA] text-[#3F5265] cursor-pointer hover:bg-[#E1EAF0] transition-colors'
              value={selectedGrain}
              onChange={handleGrainChange}
            >
              <option value='PT1H'>Last 1 Hour</option>
              <option value='PT24H'>Last 24 Hours</option>
              <option value='PT168H'>Last 7 Days</option>
              <option value='PT720H'>Last 30 Days</option>
            </select>
            {onRefresh && (
              <button
                onClick={onRefresh}
                className='p-2 text-gray-500 hover:text-teal-600 transition-colors'
                title="Refresh data"
              >
                <FiRefreshCwComponent size={18} />
              </button>
            )}
          </div>
        </div>
        <div className='px-6 py-12 text-center text-gray-500'>
          <p className='text-lg font-medium'>No campaigns data available</p>
          <p className='text-sm mt-2'>Try adjusting your filters or check back later</p>
        </div>
      </div>
    );
  }

  return (
    <div className='bg-white rounded-md mt-4 border border-gray-200 shadow-sm'>
      {/* Tabs & Filters */}
      <div className='flex justify-between items-center px-6 pt-4'>
        <div className='flex space-x-6 text-[20px] font-semibold'>
          Campaigns ({totalCount})
        </div>

        {/* Date Filter + Export */}
        <div className='flex items-center space-x-4'>
          <select
            className='border rounded p-2 text-[14px] border-[#E1EAF0] bg-[#F5F8FA] text-[#3F5265] cursor-pointer hover:bg-[#E1EAF0] transition-colors'
            value={selectedGrain}
            onChange={handleGrainChange}
          >
            <option value='PT1H'>Last 1 Hour</option>
            <option value='PT24H'>Last 24 Hours</option>
            <option value='PT168H'>Last 7 Days</option>
            <option value='PT720H'>Last 30 Days</option>
            <option value='PT8760H'>Last 1 Year</option>
          </select>

          {onRefresh && (
            <button
              onClick={onRefresh}
              className='p-2 text-gray-500 hover:text-teal-600 transition-colors'
              title="Refresh data"
            >
              <FiRefreshCwComponent size={18} />
            </button>
          )}

          <button
            onClick={handleExport}
            disabled={isExporting}
            className={`px-4 py-2 rounded-sm text-sm font-medium flex items-center text-white bg-orange-500 hover:bg-orange-600 transition-colors ${isExporting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            style={{ fontSize: '14px' }}
          >
            {isExporting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Exporting...
              </>
            ) : (
              'Export'
            )}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className='px-6'>
        <table className='w-full text-left mt-4 border border-gray-200'>
          <thead className='bg-gray-100'>
            <tr
              className='text-gray-600 text-sm border-b'
              style={{ fontSize: '12px', color: '#657584' }}
            >
              <th
                className='px-6 py-3 cursor-pointer font-normal'
                onClick={() => requestSort('name')}
              >
                <div className='flex items-center gap-1'>
                  NAME {getSortIcon('name')}
                </div>
              </th>
              <th
                className='px-6 py-3 cursor-pointer font-normal'
                onClick={() => requestSort('status')}
              >
                <div className='flex items-center gap-1'>
                  STATUS {getSortIcon('status')}
                </div>
              </th>
              <th
                className='px-6 py-3 cursor-pointer font-normal'
                onClick={() => requestSort('triggerType')}
              >
                <div className='flex items-center gap-1'>
                  TRIGGER TYPE {getSortIcon('triggerType')}
                </div>
              </th>
              <th className='px-6 py-3 font-normal'>SENT AT</th>
              <th className='px-6 py-3 font-normal'>DELIVERIES</th>
              <th className='px-6 py-3 font-normal'>OPEN RATE</th>
              <th className='px-6 py-3 font-normal'>CLICK RATE</th>
            </tr>
          </thead>
          <tbody style={{ color: '#3F5265', fontSize: '14px' }}>
            {paginatedCampaigns.map((c) => (
              <tr key={c.id} className='border-b hover:bg-gray-50'>
                <td className='px-6 py-4 hover:text-teal-600 cursor-pointer'>
                  {c.name}
                </td>
                <td className='px-6 py-4'>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                      c.status
                    )}`}
                  >
                    {c.status}
                  </span>
                </td>
                <td className='px-6 py-4'>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium`}
                    style={{ backgroundColor: '#F5F8FA' }}
                  >
                    {c.triggerType || '—'}
                  </span>
                </td>
                <td className='px-6 py-4'>{c.sentAt || '—'}</td>
                <td className='px-6 py-4'>{c.recipients}</td>
                <td className='px-6 py-4'>{c.openRate}</td>
                <td className='px-6 py-4'>{c.clickRate}</td>
              </tr>
            ))}
            {paginatedCampaigns.length === 0 && (
              <tr>
                <td colSpan={9} className='px-6 py-6 text-center text-gray-500'>
                  No campaigns found for "{activeTab}" with current filter
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Footer with Modern Pagination - Same style as TemplatesGallery */}
      {sortedCampaigns.length > 0 && (
        <div className='flex items-center justify-between px-6 py-4 text-sm text-gray-500 border-t border-gray-200'>
          <span>
            Showing {startItem} to {endItem} of {totalCountFiltered} campaigns
          </span>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 0}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              {(() => {
                const pages = [];
                const total = totalPages;
                const current = currentPage + 1;
                const delta = 1;

                pages.push(1);

                let rangeStart = Math.max(2, current - delta);
                let rangeEnd = Math.min(total - 1, current + delta);

                if (rangeStart > 2) {
                  pages.push('...');
                }

                for (let i = rangeStart; i <= rangeEnd; i++) {
                  pages.push(i);
                }

                if (rangeEnd < total - 1) {
                  pages.push('...');
                }

                if (total > 1) {
                  pages.push(total);
                }

                return pages.map((page, index) => {
                  if (page === '...') {
                    return (
                      <span
                        key={`ellipsis-${index}`}
                        className="px-3 py-2 text-sm text-gray-500"
                      >
                        …
                      </span>
                    );
                  }

                  const pageNumber = page as number;
                  const isActive = pageNumber === current;

                  return (
                    <button
                      key={pageNumber}
                      onClick={() => handlePageChange(pageNumber - 1)}
                      className={`px-4 py-2 text-sm font-medium rounded-lg ${isActive
                        ? 'bg-orange-500 text-white'
                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                    >
                      {pageNumber}
                    </button>
                  );
                });
              })()}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage + 1 === totalPages}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CampaignsTable;